import { LitElement, html } from 'lit'
import '../components/modal-conta.js'
import '../components/modal-categoria.js'
import '../components/modal-responsavel.js'
import '../components/modal-tag.js'
import '../components/modal-premium.js'
import '../components/modal-confirm.js'
import '../components/search-filter.js'
import '../components/premium-guard.js'
import '../components/version-display.js'
import { contaCacheService } from '../core/services/conta-cache.service.js'
import { responsavelCacheService } from '../core/services/responsavel-cache.service.js'
import { categoriaCacheService } from '../core/services/categoria-cache.service.js'
import { tagCacheService } from '../core/services/tag-cache.service.js'
import { orcamentoCacheService } from '../core/services/orcamento-cache.service.js'
import { premiumService } from '../core/services/premium.service.js'
import { apiService } from '../core/services/api.service.js'
import { cacheCleanerService } from '../core/services/cache-cleaner.service.js'
import { alertService } from '../core/services/alert.service.js'
import { cacheService } from '../core/services/cache.service.js'
import { ProtectedPageMixin } from '../core/mixins/protected-page.mixin.js'

/**
 * Página de configurações do sistema
 * Centraliza o gerenciamento de todos os dados mestres
 */
class Configuracoes extends ProtectedPageMixin(LitElement) {
  static properties = {
    accounts: { type: Array },
    categories: { type: Array },
    responsaveis: { type: Array },
    tags: { type: Array },
    recurringTransactions: { type: Array },
    notificationsEnabled: { type: Boolean },
    loading: { type: Boolean },
    limpandoCache: { type: Boolean },
    isAdmin: { type: Boolean },
    adminUsers: { type: Array },
    adminLoading: { type: Boolean },
    adminFilter: { type: String },
    authError: { type: String },
    /** @type {string|null} ID do item em processo de exclusão (exibe spinner no botão) */
    _deletingItemId: { type: String, state: true }
  }

  createRenderRoot() {
    return this
  }

  constructor() {
    super()
    this.accounts = []
    this.categories = []
    this.responsaveis = []
    this.tags = []
    this.recurringTransactions = []
    this.currentUserEmail = ''
    this.notificationsEnabled = false
    this.loading = false
    this.limpandoCache = false
    this.isAdmin = false
    this.adminUsers = []
    this.adminLoading = false
    this.adminFilter = ''
    /** @type {string|null} Mensagem de erro de autenticação (timeout ou falha) */
    this.authError = null
    /** @type {string|null} ID do item sendo excluído (controla spinner no botão de exclusão) */
    this._deletingItemId = null
    
    // Inicializar repositórios com arquétipo PWA
    // SyncDecorator(CacheDecorator(OfflineDecorator(BaseRepository)))
    this.contaService = contaCacheService // Usando cache inteligente para contas
    this.categoriaService = categoriaCacheService // Usando cache inteligente para categorias
    this.responsavelService = responsavelCacheService // Usando cache inteligente para responsáveis
    this.tagService = tagCacheService // Usando cache inteligente para tags
    
    this._getCurrentUserEmail()
    
    // Bind dos handlers de cache para remoção correta no disconnectedCallback
    this._boundHandleContasCacheUpdated = this._handleContasCacheUpdated.bind(this)
    this._boundHandleResponsaveisCacheUpdated = this._handleResponsaveisCacheUpdated.bind(this)
    this._boundHandleCategoriasCacheUpdated = this._handleCategoriasCacheUpdated.bind(this)
    this._boundHandleTagsCacheUpdated = this._handleTagsCacheUpdated.bind(this)
    this._boundHandleOrcamentosCacheUpdated = this._handleOrcamentosCacheUpdated.bind(this)
    
    // Escutar eventos de cache atualizado
    window.addEventListener('contas-cache-updated', this._boundHandleContasCacheUpdated)
    window.addEventListener('responsaveis-cache-updated', this._boundHandleResponsaveisCacheUpdated)
    window.addEventListener('categorias-cache-updated', this._boundHandleCategoriasCacheUpdated)
    window.addEventListener('tags-cache-updated', this._boundHandleTagsCacheUpdated)
    window.addEventListener('orcamentos-cache-updated', this._boundHandleOrcamentosCacheUpdated)
    
    // Escutar mudanças de autenticação
    window.addEventListener('auth-state-changed', (e) => {
      if (e.detail.isAuthenticated && e.detail.user) {
        this.currentUserEmail = e.detail.user.email || ''
        this.requestUpdate()
      }
    })
  }

  /**
   * Conecta componente ao DOM e inicia carregamento de dados
   * protegido pelo ProtectedPageMixin (aguarda autenticação).
   */
  connectedCallback () {
    super.connectedCallback()
    this._loadWithAuthGuard(async () => {
      await this._loadData()
    })
  }

  /**
   * Remove listeners de eventos ao desconectar o componente do DOM
   * Previne memory leaks e chamadas a componentes destruídos
   */
  disconnectedCallback () {
    super.disconnectedCallback()
    window.removeEventListener('contas-cache-updated', this._boundHandleContasCacheUpdated)
    window.removeEventListener('responsaveis-cache-updated', this._boundHandleResponsaveisCacheUpdated)
    window.removeEventListener('categorias-cache-updated', this._boundHandleCategoriasCacheUpdated)
    window.removeEventListener('tags-cache-updated', this._boundHandleTagsCacheUpdated)
    window.removeEventListener('orcamentos-cache-updated', this._boundHandleOrcamentosCacheUpdated)
  }

  async _loadData() {
    this.loading = true
    try {
      const [contas, categorias, responsaveis, tags] = await Promise.all([
        this.contaService.listar(), // Cache inteligente (sem GET se válido)
        this.categoriaService.listar(), // Cache inteligente (sem GET se válido)
        this.responsavelService.listar(), // Cache inteligente (sem GET se válido)
        this.tagService.listar() // Cache inteligente (sem GET se válido)
      ])

      this.accounts = contas
      this.categories = categorias
      this.responsaveis = responsaveis
      this.tags = tags
      this.recurringTransactions = []
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    } finally {
      this.loading = false
    }

    // Verificar se usuário é admin (não bloqueia carregamento principal)
    this._checkAdmin()
  }

  _formatCurrency(amount) {
    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
  }

  _getAmountClass(amount) {
    return amount >= 0 ? 'has-text-success' : 'has-text-danger'
  }

  _formatAmount(amount) {
    const formatted = this._formatCurrency(Math.abs(amount))
    return amount >= 0 ? formatted : `-${formatted}`
  }

  _addAccount() {
    const modal = this.renderRoot.querySelector('modal-conta')
    modal.show()
  }

  _editAccount(accountId) {
    const account = this.accounts.find(a => a.id === accountId)
    if (account) {
      const modal = this.renderRoot.querySelector('modal-conta')
      modal.show(account)
    }
  }

  /**
   * Exclui uma conta com feedback visual de loading no botão
   * Substitui ícone por spinner e desabilita o botão durante a operação.
   * Mantém demais botões (editar, outros itens) habilitados.
   * Em caso de erro, restaura o botão e exibe alertService.error().
   * @param {string} accountId - ID da conta a ser excluída
   * @private
   */
  async _deleteAccount(accountId) {
    if (!confirm('Tem certeza que deseja excluir esta conta?')) return
    
    this._deletingItemId = accountId
    try {
      await this.contaService.excluir(accountId)
      // Item removido via evento contas-cache-updated (fluxo padrão)
    } catch (error) {
      alertService.error('Erro ao excluir. Tente novamente.')
    } finally {
      this._deletingItemId = null
    }
  }

  _addResponsavel() {
    const modal = this.renderRoot.querySelector('modal-responsavel')
    modal.show(null, false, this.currentUserEmail)
  }

  _editResponsavel(responsavelId) {
    const responsavel = this.responsaveis.find(r => r.id === responsavelId)
    if (responsavel) {
      const modal = this.renderRoot.querySelector('modal-responsavel')
      const isCurrentUser = this._isCurrentUser(responsavel)
      modal.show(responsavel, isCurrentUser, this.currentUserEmail)
    }
  }

  /**
   * Exclui um responsável com feedback visual de loading no botão
   * Substitui ícone por spinner e desabilita o botão durante a operação.
   * Mantém demais botões (editar, outros itens) habilitados.
   * Em caso de erro, restaura o botão e exibe alertService.error().
   * @param {string} responsavelId - ID do responsável a ser excluído
   * @private
   */
  async _deleteResponsavel(responsavelId) {
    if (!confirm('Tem certeza que deseja excluir este responsável?')) return
    
    this._deletingItemId = responsavelId
    try {
      await this.responsavelService.excluir(responsavelId)
      // Item removido via evento responsaveis-cache-updated (fluxo padrão)
    } catch (error) {
      alertService.error('Erro ao excluir. Tente novamente.')
    } finally {
      this._deletingItemId = null
    }
  }

  /**
   * Obtém email do usuário logado
   * @private
   */
  _getCurrentUserEmail() {
    // Importar authService dinamicamente para evitar circular dependency
    import('../core/services/auth.service.js').then(module => {
      const authService = module.default
      const user = authService.getCurrentUser()
      if (user && user.email) {
        this.currentUserEmail = user.email
        this.requestUpdate()
      }
    })
  }

  /**
   * Verifica se responsável é o usuário logado
   * @param {Object} responsavel - Responsável a verificar
   * @returns {boolean} true se for o usuário logado
   * @private
   */
  _isCurrentUser(responsavel) {
    // Tentar obter email atual se estiver vazio
    if (!this.currentUserEmail) {
      this._getCurrentUserEmail()
    }
    
    return responsavel.email === this.currentUserEmail
  }

  _addCategory() {
    const modal = this.renderRoot.querySelector('modal-categoria')
    modal.show()
  }

  _editCategory(categoryId) {
    const category = this.categories.find(c => c.id === categoryId)
    if (category) {
      const modal = this.renderRoot.querySelector('modal-categoria')
      modal.show(category)
    }
  }

  /**
   * Exclui uma categoria com feedback visual de loading no botão
   * Substitui ícone por spinner e desabilita o botão durante a operação.
   * Mantém demais botões (editar, outros itens) habilitados.
   * Em caso de erro, restaura o botão e exibe alertService.error().
   * @param {string} categoryId - ID da categoria a ser excluída
   * @private
   */
  async _deleteCategory(categoryId) {
    if (!confirm('Tem certeza que deseja excluir esta categoria?')) return
    
    this._deletingItemId = categoryId
    try {
      await this.categoriaService.excluir(categoryId)
      // Item removido via evento categorias-cache-updated (fluxo padrão)
    } catch (error) {
      alertService.error('Erro ao excluir. Tente novamente.')
    } finally {
      this._deletingItemId = null
    }
  }

  _addTag() {
    const modal = this.renderRoot.querySelector('modal-tag')
    modal.show()
  }

  _editTag(tagId) {
    const tag = this.tags.find(t => t.id === tagId)
    if (tag) {
      const modal = this.renderRoot.querySelector('modal-tag')
      modal.show(tag)
    }
  }

  /**
   * Exclui uma tag com feedback visual de loading no botão
   * Substitui ícone por spinner e desabilita o botão durante a operação.
   * Mantém demais botões (editar, outros itens) habilitados.
   * Em caso de erro, restaura o botão e exibe alertService.error().
   * @param {string} tagId - ID da tag a ser excluída
   * @private
   */
  async _deleteTag(tagId) {
    if (!confirm('Tem certeza que deseja excluir esta tag?')) return
    
    this._deletingItemId = tagId
    try {
      await this.tagService.excluir(tagId)
      // Item removido via evento tags-cache-updated (fluxo padrão)
    } catch (error) {
      alertService.error('Erro ao excluir. Tente novamente.')
    } finally {
      this._deletingItemId = null
    }
  }

  _inviteUser() {
    console.log('Convidar usuário')
  }

  _toggleNotifications() {
    this.notificationsEnabled = !this.notificationsEnabled
  }

  _testNotification() {
    console.log('Testar notificação')
  }

  /**
   * Handler do clique no botão "Sair"
   * Exibe o modal de confirmação antes de executar o logout
   * @private
   */
  _logout () {
    const modal = this.renderRoot.querySelector('[data-cy="modal-confirmacao-logout"]')
    if (modal) {
      modal.show(
        'Deseja realmente sair da aplicação?',
        'Sair',
        'Sair',
        'Cancelar',
        'danger'
      )
    }
  }

  /**
   * Callback de confirmação do modal de logout
   * Limpa cache premium e executa logout via authService, redirecionando para login
   * @private
   * @returns {Promise<void>}
   */
  async _confirmarLogout () {
    premiumService.clearCache()
    const { default: authService } = await import('../core/services/auth.service.js')
    await authService.logout()
    window.location.hash = '#/login'
  }

  /**
   * Callback de cancelamento do modal de logout
   * Fecha o modal sem executar nenhuma ação
   * @private
   */
  _cancelarLogout () {
    // Nenhuma ação necessária — o modal já fecha automaticamente
  }

  /**
   * Handler do clique no botão "Limpar Cache"
   * Exibe o modal de confirmação antes de executar a limpeza
   * @private
   */
  _handleLimparCache () {
    const modal = this.renderRoot.querySelector('[data-cy="modal-confirmacao-cache"]')
    if (modal) {
      modal.show(
        'Deseja realmente limpar o cache? A página será recarregada.',
        'Limpar Cache',
        'Confirmar',
        'Cancelar',
        'warning'
      )
    }
  }

  /**
   * Callback de confirmação do modal de limpeza de cache
   * Ativa estado de loading, executa limpeza via CacheCleanerService e avalia resultado.
   * Se ao menos uma etapa teve sucesso, força reload da página.
   * Se todas falharam, exibe mensagem de erro e restaura o botão.
   * @private
   * @returns {Promise<void>}
   */
  async _confirmarLimpezaCache () {
    this.limpandoCache = true

    try {
      const resultado = await cacheCleanerService.executarLimpeza()

      if (resultado.sucesso === true) {
        // Ao menos uma etapa de limpeza completou — recarregar a página
        this._recarregarPagina()
      } else {
        // Todas as etapas falharam — informar o usuário e restaurar botão
        window.alert('Não foi possível completar a limpeza de cache. Tente novamente ou limpe manualmente pelo navegador.')
        this.limpandoCache = false
      }
    } catch (erro) {
      // Erro inesperado na execução — informar o usuário e restaurar botão
      console.error('Erro inesperado ao executar limpeza de cache:', erro)
      window.alert('Não foi possível completar a limpeza de cache. Tente novamente ou limpe manualmente pelo navegador.')
      this.limpandoCache = false
    }
  }

  /**
   * Executa o reload da página via window.location.reload().
   * Extraído em método separado para permitir override em testes unitários,
   * já que window.location.reload não pode ser stubbed no navegador.
   * @private
   */
  _recarregarPagina () {
    window.location.reload()
  }

  /**
   * Callback de cancelamento do modal de limpeza de cache
   * Fecha o modal sem executar nenhuma ação
   * @private
   */
  _cancelarLimpezaCache () {
    // Nenhuma ação necessária — o modal já fecha automaticamente
  }

  /**
   * Manipula evento de cache de contas atualizado
   * Lê dados do cache local de forma síncrona (sem GET redundante)
   * Implementa guard contra null/undefined para preservar valor atual
   * @private
   */
  _handleContasCacheUpdated() {
    const contas = cacheService.get('contas')
    if (contas != null) {
      this.accounts = contas
      this.requestUpdate()
    }
  }

  /**
   * Manipula evento de cache de responsáveis atualizado
   * Lê dados do cache local de forma síncrona (sem GET redundante)
   * Implementa guard contra null/undefined para preservar valor atual
   * @private
   */
  _handleResponsaveisCacheUpdated() {
    const responsaveis = cacheService.get('responsaveis')
    if (responsaveis != null) {
      this.responsaveis = responsaveis
      this.requestUpdate()
    }
  }

  /**
   * Manipula evento de cache de categorias atualizado
   * Lê dados do cache local de forma síncrona (sem GET redundante)
   * Implementa guard contra null/undefined para preservar valor atual
   * @private
   */
  _handleCategoriasCacheUpdated() {
    const categorias = cacheService.get('categorias')
    if (categorias != null) {
      this.categories = categorias
      this.requestUpdate()
    }
  }

  /**
   * Manipula evento de cache de tags atualizado
   * Lê dados do cache local de forma síncrona (sem GET redundante)
   * Implementa guard contra null/undefined para preservar valor atual
   * @private
   */
  _handleTagsCacheUpdated() {
    const tags = cacheService.get('tags')
    if (tags != null) {
      this.tags = tags
      this.requestUpdate()
    }
  }

  /**
   * Manipula evento de cache de orçamentos atualizado
   * Orçamentos não são exibidos diretamente nesta página,
   * mas o listener é mantido para futuras expansões
   * @private
   */
  _handleOrcamentosCacheUpdated() {
    // Orçamentos serão carregados quando necessário
    this.requestUpdate()
  }

  /**
   * Verifica se o usuário atual é administrador
   * Consulta o premiumService que já retorna dados do perfil incluindo campo admin
   * Se for admin, carrega a lista de usuários automaticamente
   * @private
   * @returns {Promise<void>}
   */
  async _checkAdmin () {
    try {
      const status = await premiumService.getPremiumStatus()
      this.isAdmin = status.admin === true
      if (this.isAdmin) {
        await this._loadAdminUsers()
      }
    } catch (error) {
      console.error('Erro ao verificar admin:', error)
      this.isAdmin = false
    }
  }

  /**
   * Carrega lista de usuários para a seção administrativa
   * Utiliza cache em sessionStorage com TTL de 5 minutos para evitar requisições desnecessárias
   * Caso o cache esteja válido, usa dados do cache; caso contrário, consulta o backend
   * @private
   * @returns {Promise<void>}
   */
  async _loadAdminUsers () {
    const cached = this._getAdminCache()
    if (cached) {
      this.adminUsers = cached
      return
    }

    this.adminLoading = true
    try {
      const response = await apiService.get('users/admin/users')
      this.adminUsers = response.data || []
      this._setAdminCache(this.adminUsers)
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
      this.adminUsers = []
    } finally {
      this.adminLoading = false
    }
  }

  /**
   * Retorna lista de usuários filtrada pelo texto de busca
   * Filtra por trecho em displayName ou email (case-insensitive)
   * @returns {Array} Lista filtrada de usuários admin
   * @private
   */
  get _filteredAdminUsers () {
    if (!this.adminFilter) return this.adminUsers
    const termo = this.adminFilter.toLowerCase()
    return this.adminUsers.filter(user => {
      const nome = (user.displayName || '').toLowerCase()
      const email = (user.email || '').toLowerCase()
      return nome.includes(termo) || email.includes(termo)
    })
  }

  /**
   * Atualiza o filtro de busca da seção admin
   * Chamado pelo componente search-filter a cada caractere digitado
   * @param {CustomEvent} e - Evento com detail.value contendo o texto digitado
   * @private
   */
  _onAdminFilterChange (e) {
    this.adminFilter = e.detail.value
  }

  /**
   * Abre o modal de ativação de premium para o usuário selecionado
   * O modal permite escolher plano e duração antes de confirmar
   * @param {string} uid - ID do usuário a ter premium ativado
   * @private
   */
  _activatePremium (uid) {
    const user = this.adminUsers.find(u => u.uid === uid)
    if (!user) return

    const modal = this.querySelector('modal-premium')
    if (modal) {
      modal.show(user)
    }
  }

  /**
   * Abre o modal em modo edição para um usuário que já é premium
   * Permite alterar plano, duração ou desativar premium
   * @param {string} uid - ID do usuário premium a ser editado
   * @private
   */
  _editPremium (uid) {
    const user = this.adminUsers.find(u => u.uid === uid)
    if (!user) return

    const modal = this.querySelector('modal-premium')
    if (modal) {
      modal.show(user)
    }
  }

  /**
   * Processa a ativação de premium após confirmação no modal
   * Chama o endpoint de ativação no backend com plano e duração escolhidos
   * Atualiza a lista local e invalida cache em caso de sucesso
   * @param {CustomEvent} e - Evento com detail { uid, plan, duration }
   * @private
   * @returns {Promise<void>}
   */
  async _handlePremiumActivate (e) {
    const { uid, plan, duration } = e.detail
    const modal = this.querySelector('modal-premium')

    try {
      await apiService.post('users/admin/premium/activate', { uid, plan, duration })
      // Atualiza lista local sem nova requisição
      this.adminUsers = this.adminUsers.map(user =>
        user.uid === uid ? { ...user, premium: true, plan, status: 'active' } : user
      )
      // Invalida cache para refletir mudança
      this._clearAdminCache()
      this.requestUpdate()
      if (modal) modal.close()
    } catch (error) {
      if (modal) modal.loading = false
      alert(error.message || 'Erro ao ativar premium')
    }
  }

  /**
   * Processa a desativação de premium após confirmação no modal
   * Chama o endpoint de desativação no backend e atualiza lista local
   * @param {CustomEvent} e - Evento com detail { uid }
   * @private
   * @returns {Promise<void>}
   */
  async _handlePremiumDeactivate (e) {
    const { uid } = e.detail
    const modal = this.querySelector('modal-premium')

    try {
      await apiService.post('users/admin/premium/deactivate', { uid })
      // Atualiza lista local — remove status premium
      this.adminUsers = this.adminUsers.map(user =>
        user.uid === uid ? { ...user, premium: false, plan: null, status: 'free', premiumExpires: null } : user
      )
      // Invalida cache para refletir mudança
      this._clearAdminCache()
      this.requestUpdate()
      if (modal) modal.close()
    } catch (error) {
      if (modal) modal.loading = false
      alert(error.message || 'Erro ao desativar premium')
    }
  }

  /**
   * Obtém dados do cache de usuários admin no sessionStorage
   * Verifica se o cache existe e se o TTL de 5 minutos não expirou
   * @private
   * @returns {Array|null} Lista de usuários do cache ou null se inválido/expirado
   */
  _getAdminCache () {
    const cached = sessionStorage.getItem('admin_users')
    if (!cached) return null
    try {
      const { data, timestamp } = JSON.parse(cached)
      // TTL de 5 minutos
      if (Date.now() - timestamp > 5 * 60 * 1000) return null
      return data
    } catch (error) {
      return null
    }
  }

  /**
   * Salva lista de usuários no cache do sessionStorage com timestamp atual
   * @param {Array} data - Lista de usuários a ser cacheada
   * @private
   */
  _setAdminCache (data) {
    sessionStorage.setItem('admin_users', JSON.stringify({
      data,
      timestamp: Date.now()
    }))
  }

  /**
   * Remove cache de usuários admin do sessionStorage
   * Utilizado após ativação de premium para forçar nova consulta
   * @private
   */
  _clearAdminCache () {
    sessionStorage.removeItem('admin_users')
  }

  render() {
    return html`
      <premium-guard>
        <section class="section">
        <div class="container" style="padding-bottom: 90px;">
          <div class="is-flex is-justify-content-space-between is-align-items-center mb-5">
            <h1 class="title is-4" data-cy="configuracoes-title">Configurações</h1>
          </div>

          <!-- Minhas Contas -->
          <div class="card mb-5" data-cy="card-contas">
            <header class="card-header">
              <div class="is-flex is-justify-content-space-between is-align-items-center" style="width: 100%; padding: 0 1rem;">
                <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
                  <span class="icon">
                    <i class="fas fa-credit-card"></i>
                  </span>
                  <p class="card-header-title">Minhas Contas</p>
                </div>
                <button 
                  class="button is-primary is-small"
                  data-cy="btn-add-account"
                  @click="${this._addAccount}"
                  style="margin-right: 0.5rem;"
                >
                  <span class="icon is-small">
                    <i class="fas fa-plus"></i>
                  </span>
                  <span>Adicionar</span>
                </button>
              </div>
            </header>
            <div class="card-content">
              <div class="content">
                ${this.loading ? html`
                  <div class="has-text-centered py-5">
                    <span class="icon is-large has-text-grey-light">
                      <i class="fas fa-spinner fa-pulse fa-2x"></i>
                    </span>
                    <p class="has-text-grey mt-3">Carregando contas...</p>
                  </div>
                ` : this.accounts.length === 0 ? html`
                  <div class="has-text-centered py-5">
                    <span class="icon is-large has-text-grey-light">
                      <i class="fas fa-credit-card fa-2x"></i>
                    </span>
                    <p class="has-text-grey mt-3 mb-2">Nenhuma conta cadastrada</p>
                    <p class="has-text-grey-light is-size-7">Adicione suas contas para começar a controlar suas finanças</p>
                  </div>
                ` : this.accounts.map(account => html`
                  <div class="box p-3 mb-3" style="border: 1px solid var(--border, #26304A); border-radius: 8px;" data-cy="account-item">
                    <div class="is-flex is-justify-content-space-between is-align-items-center">
                      <div>
                        <div class="has-text-weight-medium" data-cy="account-name">${account.nome}</div>
                        <div class="is-size-7 has-text-grey" data-cy="account-type">${account.tipo}</div>
                      </div>
                      <div class="is-flex is-align-items-center" style="gap: 1rem;">
                        <span class="${this._getAmountClass(account.saldoAtual || account.saldo || 0)}" data-cy="account-balance">
                          ${this._formatAmount(account.saldoAtual || account.saldo || 0)}
                        </span>
                        <div class="is-flex" style="gap: 0.5rem; margin-right: 0.5rem;">
                          <button 
                            class="button is-small is-ghost"
                            data-cy="btn-edit-account"
                            @click="${() => this._editAccount(account.id)}"
                          >
                            <span class="icon is-small">
                              <i class="fas fa-pen"></i>
                            </span>
                          </button>
                          <button 
                            class="button is-small is-ghost"
                            data-cy="btn-delete-account"
                            ?disabled="${this._deletingItemId === account.id}"
                            @click="${() => this._deleteAccount(account.id)}"
                          >
                            <span class="icon is-small">
                              ${this._deletingItemId === account.id
                                ? html`<i class="fas fa-spinner fa-pulse"></i>`
                                : html`<i class="fas fa-trash"></i>`
                              }
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                `)}
              </div>
            </div>
          </div>

          <!-- Responsáveis -->
          <div class="card mb-5" data-cy="card-responsaveis">
            <header class="card-header">
              <div class="is-flex is-justify-content-space-between is-align-items-center" style="width: 100%; padding: 0 1rem;">
                <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
                  <span class="icon">
                    <i class="fas fa-users"></i>
                  </span>
                  <p class="card-header-title">Responsáveis</p>
                </div>
                <button 
                  class="button is-primary is-small"
                  data-cy="btn-add-responsavel"
                  @click="${this._addResponsavel}"
                  style="margin-right: 0.5rem;"
                >
                  <span class="icon is-small">
                    <i class="fas fa-plus"></i>
                  </span>
                  <span>Adicionar</span>
                </button>
              </div>
            </header>
            <div class="card-content">
              <div class="content">
                ${this.responsaveis.length === 0 ? html`
                  <div class="has-text-centered py-5">
                    <span class="icon is-large has-text-grey-light">
                      <i class="fas fa-users fa-2x"></i>
                    </span>
                    <p class="has-text-grey mt-3 mb-2">Nenhum responsável cadastrado</p>
                    <p class="has-text-grey-light is-size-7">Adicione responsáveis para dividir gastos e organizar transações</p>
                  </div>
                ` : this.responsaveis.filter(r => r).map(responsavel => html`
                  <div class="box p-3 mb-3" style="border: 1px solid var(--border, #26304A); border-radius: 8px;" data-cy="responsavel-item">
                    <div class="is-flex is-justify-content-space-between is-align-items-center">
                      <div>
                        <div class="has-text-weight-medium" data-cy="responsavel-nome">${responsavel.nomeExibicao || responsavel.nome}</div>
                        <div class="is-size-7 has-text-grey" data-cy="responsavel-contato">
                          ${responsavel.email ? responsavel.email : ''}
                          ${responsavel.email && responsavel.telefone ? ' • ' : ''}
                          ${responsavel.telefone ? responsavel.telefone : ''}
                        </div>
                      </div>
                      <div class="is-flex" style="gap: 0.5rem; margin-right: 0.5rem;">
                        <button 
                          class="button is-small is-ghost"
                          data-cy="btn-edit-responsavel"
                          @click="${() => this._editResponsavel(responsavel.id)}"
                        >
                          <span class="icon is-small">
                            <i class="fas fa-pen"></i>
                          </span>
                        </button>
                        ${!this._isCurrentUser(responsavel) ? html`
                          <button 
                            class="button is-small is-ghost"
                            data-cy="btn-delete-responsavel"
                            ?disabled="${this._deletingItemId === responsavel.id}"
                            @click="${() => this._deleteResponsavel(responsavel.id)}"
                          >
                            <span class="icon is-small">
                              ${this._deletingItemId === responsavel.id
                                ? html`<i class="fas fa-spinner fa-pulse"></i>`
                                : html`<i class="fas fa-trash"></i>`
                              }
                            </span>
                          </button>
                        ` : ''}
                      </div>
                    </div>
                  </div>
                `)}
              </div>
            </div>
          </div>

          <!-- Minhas Categorias -->
          <div class="card mb-5" data-cy="card-categorias">
            <header class="card-header">
              <div class="is-flex is-justify-content-space-between is-align-items-center" style="width: 100%; padding: 0 1rem;">
                <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
                  <span class="icon">
                    <i class="fas fa-tag"></i>
                  </span>
                  <p class="card-header-title">Minhas Categorias</p>
                </div>
                <button 
                  class="button is-primary is-small"
                  data-cy="btn-add-category"
                  @click="${this._addCategory}"
                  style="margin-right: 0.5rem;"
                >
                  <span class="icon is-small">
                    <i class="fas fa-plus"></i>
                  </span>
                  <span>Adicionar</span>
                </button>
              </div>
            </header>
            <div class="card-content">
              <div class="content">
                ${this.categories.length === 0 ? html`
                  <div class="has-text-centered py-5">
                    <span class="icon is-large has-text-grey-light">
                      <i class="fas fa-tag fa-2x"></i>
                    </span>
                    <p class="has-text-grey mt-3 mb-2">Nenhuma categoria cadastrada</p>
                    <p class="has-text-grey-light is-size-7">Crie categorias para organizar suas receitas e despesas</p>
                  </div>
                ` : this.categories.map(category => html`
                  <div class="box p-3 mb-3" style="border: 1px solid var(--border, #26304A); border-radius: 8px;" data-cy="category-item">
                    <div class="is-flex is-justify-content-space-between is-align-items-center">
                      <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
                        <span class="is-size-5">${category.emoji}</span>
                        <div>
                          <div class="has-text-weight-medium" data-cy="category-name">${category.nome}</div>
                          <div class="is-size-7 has-text-grey" data-cy="category-type">${category.tipo}</div>
                        </div>
                      </div>
                      <div class="is-flex" style="gap: 0.5rem; margin-right: 0.5rem;">
                        <button 
                          class="button is-small is-ghost"
                          data-cy="btn-edit-category"
                          @click="${() => this._editCategory(category.id)}"
                        >
                          <span class="icon is-small">
                            <i class="fas fa-pen"></i>
                          </span>
                        </button>
                        <button 
                          class="button is-small is-ghost"
                          data-cy="btn-delete-category"
                          ?disabled="${this._deletingItemId === category.id}"
                          @click="${() => this._deleteCategory(category.id)}"
                        >
                          <span class="icon is-small">
                            ${this._deletingItemId === category.id
                              ? html`<i class="fas fa-spinner fa-pulse"></i>`
                              : html`<i class="fas fa-trash"></i>`
                            }
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                `)}
              </div>
            </div>
          </div>

          <!-- Minhas Tags -->
          <div class="card mb-5" data-cy="card-tags">
            <header class="card-header">
              <div class="is-flex is-justify-content-space-between is-align-items-center" style="width: 100%; padding: 0 1rem;">
                <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
                  <span class="icon">
                    <i class="fas fa-tags"></i>
                  </span>
                  <p class="card-header-title">Minhas Tags</p>
                </div>
                <button 
                  class="button is-primary is-small"
                  data-cy="btn-add-tag"
                  @click="${this._addTag}"
                  style="margin-right: 0.5rem;"
                >
                  <span class="icon is-small">
                    <i class="fas fa-plus"></i>
                  </span>
                  <span>Adicionar</span>
                </button>
              </div>
            </header>
            <div class="card-content">
              <div class="content">
                ${this.loading ? html`
                  <div class="has-text-centered py-5">
                    <span class="icon is-large has-text-grey-light">
                      <i class="fas fa-spinner fa-pulse fa-2x"></i>
                    </span>
                    <p class="has-text-grey mt-3">Carregando tags...</p>
                  </div>
                ` : this.tags.length === 0 ? html`
                  <div class="has-text-centered py-5">
                    <span class="icon is-large has-text-grey-light">
                      <i class="fas fa-tags fa-2x"></i>
                    </span>
                    <p class="has-text-grey mt-3 mb-2">Nenhuma tag cadastrada</p>
                    <p class="has-text-grey-light is-size-7">Adicione tags para marcar e filtrar suas transações</p>
                  </div>
                ` : this.tags.map(tag => html`
                  <div class="box p-3 mb-3" style="border: 1px solid var(--border, #26304A); border-radius: 8px;" data-cy="tag-item">
                    <div class="is-flex is-justify-content-space-between is-align-items-center">
                      <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
                        <span class="tag is-medium" style="background-color: ${tag.cor || '#3273dc'}; color: white;">${tag.nome}</span>
                      </div>
                      <div class="is-flex" style="gap: 0.5rem; margin-right: 0.5rem;">
                        <button 
                          class="button is-small is-ghost"
                          data-cy="btn-edit-tag"
                          @click="${() => this._editTag(tag.id)}"
                        >
                          <span class="icon is-small">
                            <i class="fas fa-pen"></i>
                          </span>
                        </button>
                        <button 
                          class="button is-small is-ghost"
                          data-cy="btn-delete-tag"
                          ?disabled="${this._deletingItemId === tag.id}"
                          @click="${() => this._deleteTag(tag.id)}"
                        >
                          <span class="icon is-small">
                            ${this._deletingItemId === tag.id
                              ? html`<i class="fas fa-spinner fa-pulse"></i>`
                              : html`<i class="fas fa-trash"></i>`
                            }
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                `)}
              </div>
            </div>
          </div>

          <!-- Transações Recorrentes -->
          <div class="card mb-5" data-cy="card-recorrentes">
            <header class="card-header">
              <div class="is-flex is-align-items-center" style="gap: 0.5rem; padding: 0 1rem;">
                <span class="icon">
                  <i class="fas fa-sync-alt"></i>
                </span>
                <p class="card-header-title">Transações Recorrentes</p>
              </div>
            </header>
            <div class="card-content">
              <div class="content">
                <div class="has-text-centered py-5">
                  <span class="icon is-large has-text-grey-light">
                    <i class="fas fa-sync-alt fa-2x"></i>
                  </span>
                  <p class="has-text-grey mt-3 mb-2">Nenhuma transação recorrente</p>
                  <p class="has-text-grey-light is-size-7">Configure transações recorrentes para automatizar lançamentos</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Notificações -->
          <div class="card mb-5" data-cy="card-notifications">
            <header class="card-header">
              <div class="is-flex is-align-items-center" style="gap: 0.5rem; padding: 0 1rem;">
                <span class="icon">
                  <i class="fas fa-bell"></i>
                </span>
                <p class="card-header-title">Notificações</p>
              </div>
            </header>
            <div class="card-content">
              <div class="content">
                <div class="field">
                  <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
                    <div>
                      <div class="has-text-weight-medium">Notificações Push</div>
                      <div class="is-size-7 has-text-grey">Receba alertas sobre transações e orçamentos</div>
                    </div>
                    <label class="switch" data-cy="switch-notifications">
                      <input type="checkbox" ?checked="${this.notificationsEnabled}" @change="${this._toggleNotifications}">
                      <span class="slider round"></span>
                    </label>
                  </div>
                  ${this.notificationsEnabled ? html`
                    <button 
                      class="button is-small is-outlined"
                      data-cy="btn-test-notification"
                      @click="${this._testNotification}"
                    >
                      <span class="icon is-small">
                        <i class="fas fa-vial"></i>
                      </span>
                      <span>Testar Notificação</span>
                    </button>
                  ` : ''}
                </div>
              </div>
            </div>
          </div>

          <!-- Ações -->
          <div class="card" data-cy="card-acoes">
            <div class="card-content" style="padding: 1.5rem; overflow: hidden;">
              <div class="content">
                <button 
                  class="button is-fullwidth mb-3"
                  style="box-sizing: border-box;"
                  data-cy="btn-invite-user"
                  @click="${this._inviteUser}"
                >
                  <span class="icon">
                    <i class="fas fa-user-plus"></i>
                  </span>
                  <span>Convidar Usuário</span>
                </button>
                <button
                  class="button is-fullwidth mb-3 has-text-warning"
                  style="box-sizing: border-box;"
                  data-cy="btn-limpar-cache"
                  ?disabled="${this.limpandoCache}"
                  @click="${this._handleLimparCache}"
                >
                  ${this.limpandoCache ? html`
                    <span class="icon">
                      <i class="fas fa-spinner fa-pulse"></i>
                    </span>
                  ` : html`
                    <span class="icon">
                      <i class="fas fa-broom"></i>
                    </span>
                  `}
                  <span>Limpar Cache</span>
                </button>
                <button 
                  class="button is-fullwidth has-text-danger"
                  style="box-sizing: border-box;"
                  data-cy="btn-logout"
                  @click="${this._logout}"
                >
                  <span class="icon">
                    <i class="fas fa-sign-out-alt"></i>
                  </span>
                  <span>Sair</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Seção Administrativa — visível apenas para admins -->
          ${this.isAdmin ? html`
            <div class="card mb-5 mt-5" data-cy="card-admin">
              <header class="card-header">
                <div class="is-flex is-align-items-center" style="gap: 0.5rem; padding: 0 1rem;">
                  <span class="icon">
                    <i class="fas fa-shield-alt"></i>
                  </span>
                  <p class="card-header-title">Administração</p>
                </div>
              </header>
              <div class="card-content">
                <div class="content">
                  ${this.adminLoading ? html`
                    <div class="has-text-centered py-5">
                      <span class="icon is-large has-text-grey-light">
                        <i class="fas fa-spinner fa-pulse fa-2x"></i>
                      </span>
                      <p class="has-text-grey mt-3">Carregando usuários...</p>
                    </div>
                  ` : html`
                    <search-filter
                      placeholder="Buscar por nome ou email..."
                      data-cy="filtro-admin"
                      @filter-change="${this._onAdminFilterChange}"
                    ></search-filter>
                    ${this._filteredAdminUsers.length === 0 && this.adminFilter ? html`
                      <p class="has-text-grey has-text-centered py-3" data-cy="admin-no-results">
                        Nenhum usuário encontrado para "${this.adminFilter}"
                      </p>
                    ` : ''}
                    ${this._filteredAdminUsers.map(user => html`
                      <div class="box p-3 mb-3" style="border: 1px solid var(--border, #26304A); border-radius: 8px;" data-cy="admin-user-item">
                        <div class="is-flex is-justify-content-space-between is-align-items-center">
                          <div>
                            <div class="has-text-weight-medium">${user.displayName || user.email}</div>
                            <div class="is-size-7 has-text-grey">${user.email}</div>
                          </div>
                          <div>
                            ${user.premium ? html`
                              <span class="tag is-success" style="cursor: pointer;" data-cy="badge-premium"
                                @click="${() => this._editPremium(user.uid)}">Premium</span>
                            ` : html`
                              <button
                                class="button is-small is-warning"
                                data-cy="btn-ativar-premium"
                                @click="${() => this._activatePremium(user.uid)}"
                              >
                                Ativar Premium
                              </button>
                            `}
                          </div>
                        </div>
                      </div>
                    `)}
                  `}
                </div>
              </div>
            </div>
          ` : ''}
        </div>

        <modal-conta></modal-conta>
        <modal-categoria></modal-categoria>
        <modal-responsavel></modal-responsavel>
        <modal-tag></modal-tag>
        <modal-premium @premium-activate="${this._handlePremiumActivate}" @premium-deactivate="${this._handlePremiumDeactivate}"></modal-premium>

        <!-- Modal de confirmação para limpeza de cache (Requisitos 3.1, 3.4) -->
        <modal-confirm
          data-cy="modal-confirmacao-cache"
          .title="${'Limpar Cache'}"
          .message="${'Deseja realmente limpar o cache? A página será recarregada.'}"
          .confirmText="${'Confirmar'}"
          .cancelText="${'Cancelar'}"
          .type="${'warning'}"
          .confirmDataCy="${'btn-confirmar-limpeza'}"
          .cancelDataCy="${'btn-cancelar-limpeza'}"
          @modal-confirmed="${this._confirmarLimpezaCache}"
          @modal-cancelled="${this._cancelarLimpezaCache}"
        ></modal-confirm>

        <!-- Modal de confirmação para logout -->
        <modal-confirm
          data-cy="modal-confirmacao-logout"
          .title="${'Sair'}"
          .message="${'Deseja realmente sair da aplicação?'}"
          .confirmText="${'Sair'}"
          .cancelText="${'Cancelar'}"
          .type="${'danger'}"
          .confirmDataCy="${'btn-confirmar-logout'}"
          .cancelDataCy="${'btn-cancelar-logout'}"
          @modal-confirmed="${this._confirmarLogout}"
          @modal-cancelled="${this._cancelarLogout}"
        ></modal-confirm>

        <!-- Componente de exibição de versão no rodapé (Requisitos 4.1, 4.4) -->
        <version-display data-cy="version-display"></version-display>
        </section>
      </premium-guard>
    `
  }
}

customElements.define('configuracoes-page', Configuracoes)